import aiLogger from './ai-logger';
import { Component } from "./component";
import { readSSEStream } from '../services/ai-stream-reader';
import { DocAskQuestionsModal } from './doc-ask-questions-modal';
import { getCsrfToken, getPageId, escapeHtml, denormalizeHtml, htmlToPlainText, fixCodeBlockEscapes, stripChatCodeWrappers } from '../services/ai-editor-utils';
import { AiThinkingManager, ToolProgressPayload } from './AiThinkingManager';

/**
 * Tipo para eventos de progresso recebidos do backend.
 */
interface ProgressEvent {
    status: 'processing' | 'streaming' | 'completed' | 'error';
    step: number;
    total: number;
    label: string;
    percent: number;
}

/**
 * Tipo para eventos de tool call recebidos do backend.
 */
interface ToolCallEvent {
    tool: string;
    status: string;
    label: string;
    details: Record<string, any>;
}

/**
 * AiActions - Componente para geracao de documentacao via IA.
 *
 * Arquitetura:
 * - O setup() registra APENAS o listener do botao da toolbar.
 * - Todos os elementos do modal sao resolvidos LAZILY no primeiro clique,
 *   garantindo que o componente 'popup' do modal ja esteja registrado
 *   pelo ComponentStore (que processa em document order).
 * - Usa as APIs corretas do PageEditor: getEditorComponent().getContent()
 *   para ler conteudo, e window.$events.emit('editor::replace', ...) para setar.
 * - Protocolo de progresso: backend envia linhas [PROGRESS]{json} antes do
 *   conteudo real. A linha [CONTENT_START] marca o inicio do conteudo.
 * - [HTML_MODE] sinaliza que o conteudo e HTML puro (sempre ativo).
 *
 * Thinking UI (v2 - DOM incremental, identico ao chat):
 * - Manipulacao DOM direta via appendChild (nunca innerHTML replace).
 * - Hierarquia: thinking steps (raciocinio) -> sub-steps (ferramentas).
 * - Chunks aparecem como novos thinking steps separadores.
 * - Animacoes reais com stagger delays.
 */
export class AiActions extends Component {

    protected modal: any = null;
    protected modalEl: HTMLElement | null = null;
    protected generateButtonInModal: HTMLElement | null = null;
    protected regenerateButton: HTMLElement | null = null;
    protected copyButton: HTMLElement | null = null;
    protected useButton: HTMLElement | null = null;
    protected contentPreview: HTMLElement | null = null;
    protected thinkingContainer: HTMLElement | null = null;
    protected templateSelect: HTMLSelectElement | null = null;
    protected customPromptInput: HTMLTextAreaElement | null = null;
    protected lastGeneratedContent: string = '';
    protected initialized: boolean = false;
    protected isGenerating: boolean = false;
    protected abortController: AbortController | null = null;
    protected cancelButton: HTMLElement | null = null;

    protected thinkingStepsEl: HTMLDivElement | null = null;
    protected thinkingBodyEl: HTMLDivElement | null = null;
    protected thinkingHeaderEl: HTMLDivElement | null = null;
    protected thinkingTitleEl: HTMLSpanElement | null = null;
    protected thinkingToggleEl: HTMLSpanElement | null = null;
    protected thinkingExpanded: boolean = true;
    protected activeThinkingStep: HTMLDivElement | null = null;
    protected toolCallCount: number = 0;
    protected docAskModal: DocAskQuestionsModal | null = null;
    protected thinkingManager: AiThinkingManager | null = null;

    setup() {
        const toolbarGenerateButton = this.$el.querySelector<HTMLButtonElement>('#ai-generate-button');
        if (!toolbarGenerateButton) {
            return;
        }

        toolbarGenerateButton.addEventListener('click', () => {
            this.openModal();
        });

        // Sincroniza o estado disabled com base no conteúdo do editor
        const syncButtonState = async () => {
            const content = await this.getEditorContent();
            const hasContent = content.replace(/<[^>]*>/g, '').trim().length > 0;
            toolbarGenerateButton.disabled = !hasContent;
            toolbarGenerateButton.title = hasContent
                ? ''
                : 'Adicione conteúdo ao editor para usar a geração com IA';
        };

        // Verificação inicial (editor pode não estar pronto imediatamente)
        setTimeout(syncButtonState, 800);

        // Re-sincroniza a cada mudança no editor
        window.$events.listen('editor-html-change', syncButtonState);
    }

    /**
     * Inicializacao lazy: resolve todos os elementos do modal e o componente popup
     * apenas quando o usuario clica no botao.
     */
    protected initModalIfNeeded(): boolean {
        if (this.initialized) {
            return true;
        }

        try {
            this.modalEl = document.getElementById('ai-review-modal');
            if (!this.modalEl) {
                aiLogger.error('[AiActions] Modal element #ai-review-modal not found in DOM.');
                return false;
            }

            this.modal = window.$components.firstOnElement(this.modalEl, 'popup');
            if (!this.modal) {
                aiLogger.error('[AiActions] Popup component not found on #ai-review-modal element.');
                return false;
            }

            this.generateButtonInModal = this.modalEl.querySelector('#ai-review-generate-button');
            this.regenerateButton = this.modalEl.querySelector('#ai-review-regenerate-button');
            this.copyButton = this.modalEl.querySelector('#ai-review-copy-button');
            this.useButton = this.modalEl.querySelector('#ai-review-use-button');
            this.contentPreview = this.modalEl.querySelector('#ai-generated-content-preview');
            this.thinkingContainer = this.modalEl.querySelector('#ai-thinking-container');
            this.templateSelect = this.modalEl.querySelector('#ai-template-select') as HTMLSelectElement;
            this.customPromptInput = this.modalEl.querySelector('#ai-custom-prompt') as HTMLTextAreaElement;
            this.cancelButton = this.modalEl.querySelector('#ai-review-cancel-button');

            if (!this.generateButtonInModal || !this.contentPreview) {
                aiLogger.error('[AiActions] Missing critical modal elements.');
                return false;
            }

            this.setupModalListeners();
            this.loadTemplates();

            // Init modal de perguntas específico para documentação
            this.docAskModal = new DocAskQuestionsModal();
            this.thinkingManager = new AiThinkingManager({
                onAfterRender: () => {
                    if (this.thinkingContainer) {
                        this.thinkingContainer.scrollTop = this.thinkingContainer.scrollHeight;
                    }
                }
            });

            this.initialized = true;
            return true;

        } catch (e) {
            aiLogger.error('[AiActions] Failed to initialize modal:', e);
            return false;
        }
    }

    /**
     * Registra os event listeners nos elementos do modal.
     * Chamado uma unica vez na primeira inicializacao.
     */
    protected setupModalListeners() {
        if (this.generateButtonInModal) {
            this.generateButtonInModal.addEventListener('click', () => this.generateContent());
        }

        if (this.regenerateButton) {
            this.regenerateButton.addEventListener('click', () => this.generateContent());
        }

        if (this.copyButton) {
            this.copyButton.addEventListener('click', () => this.copyContent());
        }

        if (this.useButton) {
            this.useButton.addEventListener('click', () => this.useContent());
        }

        if (this.cancelButton) {
            this.cancelButton.addEventListener('click', () => {
                if (this.isGenerating) {
                    this.cancelGeneration(false);
                } else if (this.modal) {
                    this.modal.hide();
                }
            });
        }
    }

    /**
     * Carrega a lista de templates de documentacao do backend.
     * Popula o select #ai-template-select dinamicamente.
     */
    protected async loadTemplates(): Promise<void> {
        if (!this.templateSelect) return;

        try {
            const resp = await window.fetch('/ai/templates', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                },
            });

            if (!resp.ok) {
                aiLogger.warn('[AiActions] Failed to load templates:', resp.status);
                return;
            }

            const data = await resp.json();
            if (!data.success || !Array.isArray(data.templates)) {
                return;
            }

            this.templateSelect.innerHTML = '';

            for (const tpl of data.templates) {
                const opt = document.createElement('option');
                opt.value = tpl.id;
                opt.textContent = tpl.name;
                if (tpl.description) {
                    opt.title = tpl.description;
                }
                // Selecionar o template padrao (is_default)
                if (tpl.is_default) {
                    opt.selected = true;
                }
                this.templateSelect.appendChild(opt);
            }

            aiLogger.info(`[AiActions] Loaded ${data.templates.length} templates.`);
        } catch (e) {
            aiLogger.warn('[AiActions] Error loading templates:', e);
        }
    }

    /**
     * Abre o modal de revisao.
     * Faz a inicializacao lazy na primeira chamada.
     */
    protected openModal() {
        if (!this.initModalIfNeeded()) {
            window.$events.emit('error', 'Erro ao inicializar o modal de IA. Recarregue a pagina.');
            return;
        }

        this.resetModalContent();
        this.modal.show();
    }

    protected resetModalContent() {
        if (this.contentPreview) {
            this.contentPreview.classList.remove('is-generating');
            this.contentPreview.innerHTML = [
                '<div class="text-center text-muted py-xl">',
                '<p class="mt-m">Selecione um modelo e clique em <strong>"Gerar"</strong> para comecar.</p>',
                '</div>'
            ].join('');
        }

        if (this.thinkingContainer) {
            this.thinkingContainer.style.display = 'none';
            this.thinkingContainer.innerHTML = '';
        }

        this.lastGeneratedContent = '';
        this.isGenerating = false;

        this.thinkingStepsEl = null;
        this.thinkingBodyEl = null;
        this.thinkingHeaderEl = null;
        this.thinkingTitleEl = null;
        this.thinkingToggleEl = null;
        this.thinkingExpanded = true;
        this.activeThinkingStep = null;
        this.toolCallCount = 0;
        
        if (this.thinkingManager) {
            this.thinkingManager.reset();
        }

        if (this.regenerateButton) this.regenerateButton.classList.add('hidden');
        if (this.copyButton) this.copyButton.classList.add('hidden');
        if (this.useButton) this.useButton.classList.add('hidden');
        if (this.generateButtonInModal) this.generateButtonInModal.classList.remove('hidden');

        // Manter template selecionado (nao resetar)
        if (this.customPromptInput) {
            this.customPromptInput.value = '';
        }

        this.cancelGeneration();
    }

    /**
     * Obtem o conteudo atual do editor via API correta do PageEditor.
     */
    protected async getEditorContent(): Promise<string> {
        try {
            const pageEditor = window.$components.first('page-editor') as any;
            if (!pageEditor) {
                return '';
            }

            const editorComponent = pageEditor.getEditorComponent();
            if (!editorComponent) {
                return '';
            }

            const content = await editorComponent.getContent();
            return content.markdown || content.html || '';
        } catch (e) {
            aiLogger.error('[AiActions] Failed to get editor content:', e);
            return '';
        }
    }

    async generateContent() {
        const templateId = this.templateSelect?.value || '';
        const customPrompt = this.customPromptInput?.value.trim() || '';
        const editorContent = await this.getEditorContent();

        await this.performGeneration(editorContent, getPageId(), customPrompt, templateId);
    }

    protected cancelGeneration(preventButtonReset: boolean = false) {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
        this.isGenerating = false;

        if (this.templateSelect) this.templateSelect.disabled = false;
        if (this.customPromptInput) this.customPromptInput.disabled = false;

        if (this.modalEl) {
            this.modalEl.dataset.preventBackgroundClose = 'false';
        }
        if (this.contentPreview && this.contentPreview.classList.contains('is-generating')) {
            this.contentPreview.classList.remove('is-generating');
            this.contentPreview.innerHTML = [
                '<div class="text-center text-neg py-xl">',
                '<div><svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-neg mb-m"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg></div>',
                '<p class="mt-m text-large"><strong>Geração Cancelada</strong></p>',
                '<p>O processo foi interrompido antes da conclusão total.</p>',
                '</div>'
            ].join('');
        }
        if (!preventButtonReset && this.generateButtonInModal) {
            this.generateButtonInModal.classList.remove('hidden');
        }
    }

    async performGeneration(userContent: string, pageId: number | null, customPrompt: string = '', templateId: string = '') {
        // Abort previous generation if exists (without resetting the button state)
        this.cancelGeneration(true);
        this.abortController = new AbortController();

        if (this.generateButtonInModal) this.generateButtonInModal.classList.add('hidden');
        if (this.regenerateButton) this.regenerateButton.classList.add('hidden');
        if (this.copyButton) this.copyButton.classList.add('hidden');
        if (this.useButton) this.useButton.classList.add('hidden');

        this.isGenerating = true;

        if (this.templateSelect) this.templateSelect.disabled = true;
        if (this.customPromptInput) this.customPromptInput.disabled = true;

        if (this.modalEl) {
            this.modalEl.dataset.preventBackgroundClose = 'true';
        }

        this.thinkingStepsEl = null;
        this.thinkingBodyEl = null;
        this.thinkingHeaderEl = null;
        this.thinkingTitleEl = null;
        this.thinkingToggleEl = null;
        this.thinkingExpanded = true;
        this.activeThinkingStep = null;
        this.toolCallCount = 0;

        if (this.thinkingManager) {
            this.thinkingManager.reset();
        }

        if (this.thinkingContainer) {
            this.thinkingContainer.style.display = 'block';
            this.thinkingContainer.innerHTML = '';
        }

        this.showGeneratingSpinner();

        // Yield to the browser to ensure the UI updates (buttons hidden, spinner shown)
        // are painted before we start the potentially blocking request setup
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

        try {
            const resp = await window.fetch('/ai/generate-content', {
                method: 'POST',
                signal: this.abortController.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                    'Accept': 'text/event-stream',
                },
                body: JSON.stringify({
                    content: userContent || ' ',
                    page_id: pageId,
                    custom_prompt: customPrompt || undefined,
                    template_id: templateId || undefined
                })
            });

            if (!resp.ok) {
                const errorText = await resp.text();
                throw new Error(errorText || `HTTP ${resp.status}`);
            }

            const rawContent = await readSSEStream(resp, {
                onProgress: (data: ProgressEvent) => this.handleProgressEvent(data),
                onToolCall: (data: ToolCallEvent) => this.handleToolCallEvent(data),
                onHtmlMode: () => aiLogger.info('[AiActions] HTML mode confirmed.'),
                onToolProgress: (data: any) => {
                    if (data.type === 'ask_questions_pending' && this.docAskModal) {
                        this.docAskModal.showQuestions({
                            requestId: data.requestId,
                            questions: data.questions,
                            context: data.context || undefined,
                        });
                    } else if (data.type === 'ask_questions_timeout' && this.docAskModal) {
                        this.docAskModal.dismiss();
                    }
                },
                onContentStart: () => {
                    this.isGenerating = false;
                    if (this.thinkingManager) this.thinkingManager.collapse(true);
                    if (this.contentPreview) {
                        this.contentPreview.classList.remove('is-generating');
                        this.contentPreview.innerHTML = '';
                    }
                },
                onContentChunk: (fullContent: string) => this.updatePreview(fullContent),
            });

            this.lastGeneratedContent = stripChatCodeWrappers(fixCodeBlockEscapes(denormalizeHtml(rawContent)));

            this.isGenerating = false;

            if (this.templateSelect) this.templateSelect.disabled = false;
            if (this.customPromptInput) this.customPromptInput.disabled = false;

            if (this.modalEl) {
                this.modalEl.dataset.preventBackgroundClose = 'false';
            }

            if (this.regenerateButton) this.regenerateButton.classList.remove('hidden');
            if (this.copyButton) this.copyButton.classList.remove('hidden');
            if (this.useButton) this.useButton.classList.remove('hidden');
            window.$events.emit('success', 'Conteudo gerado com sucesso!');

        } catch (err: any) {
            if (err.name === 'AbortError' || (this.abortController && this.abortController.signal.aborted)) {
                aiLogger.info('[AiActions] Generation aborted by user.');
                return;
            }

            const msg = err?.message || 'Erro desconhecido';
            this.isGenerating = false;
            if (this.modalEl) {
                this.modalEl.dataset.preventBackgroundClose = 'false';
            }
            window.$events.emit('error', 'Erro ao gerar conteudo: ' + msg);
            if (this.contentPreview) {
                this.contentPreview.classList.remove('is-generating');
                this.contentPreview.innerHTML = [
                    '<div class="text-center text-neg py-m">',
                    '<p>Erro ao gerar conteudo.</p>',
                    '<p class="text-small">' + escapeHtml(msg) + '</p>',
                    '</div>'
                ].join('');
            }
            if (this.generateButtonInModal) this.generateButtonInModal.classList.remove('hidden');

            if (this.templateSelect) this.templateSelect.disabled = false;
            if (this.customPromptInput) this.customPromptInput.disabled = false;
        }
    }

    /**
     * Processa evento de progresso recebido do backend.
     */
    protected handleProgressEvent(data: ProgressEvent): void {
        if (!this.thinkingManager || !this.thinkingContainer) return;
        this.thinkingManager.handleSearchStatus(data.label, this.thinkingContainer, null as any);
    }

    /**
     * Processa evento de tool call recebido do backend.
     */
    protected handleToolCallEvent(data: ToolCallEvent): void {
        if (!this.thinkingManager || !this.thinkingContainer) return;

        if (data.tool === 'system_init') {
            this.thinkingManager.handleSearchStatus(data.details?.label || data.label || 'Preparando...', this.thinkingContainer, null as any);
            return;
        }

        if (data.tool === 'chunk_progress' && data.status === 'chunk_start') {
            const label = data.details?.label || data.label || 'Processando...';
            this.thinkingManager.handleProgress({
                type: 'tool_start',
                tool: 'chunk_progress',
                description: label,
                meta: { icon: 'athena-file', label, group_label: 'Lendo conteúdo...' }
            }, this.thinkingContainer, null as any);
            return;
        }

        if (data.tool === 'think' && data.status === 'thinking') {
            const thought = data.details?.thought || data.label;
            this.thinkingManager.handleProgress({
                type: 'reasoning_step',
                message: thought
            }, this.thinkingContainer, null as any);
            return;
        }

        if (data.status === 'executing') {
            this.toolCallCount++;
            this.thinkingManager.handleProgress({
                type: 'tool_start',
                tool: data.tool,
                description: data.label,
                meta: data.details?.meta
            }, this.thinkingContainer, null as any);
            return;
        }

        if (data.status === 'completed') {
            const resultsCount = data.details?.results_count ?? 0;
            const description = data.label || (resultsCount > 0
                ? `${resultsCount} resultado${resultsCount > 1 ? 's' : ''}`
                : 'Nenhum resultado');
            this.thinkingManager.handleProgress({
                type: 'tool_end',
                tool: data.tool,
                description: description,
                results_count: resultsCount,
                meta: data.details?.meta
            }, this.thinkingContainer, null as any);
            return;
        }
    }

    /**
     * Mostra spinner de "gerando" no content preview.
     */
    protected showGeneratingSpinner(): void {
        if (!this.contentPreview) return;
        this.contentPreview.classList.add('is-generating');
        this.contentPreview.innerHTML = `
            <div class="ai-generating-indicator">
                <div class="ai-generating-spinner"></div>
                <span class="ai-generating-text">Gerando documentacao...</span>
            </div>
        `;
    }



    /**
     * Atualiza o preview com conteudo HTML.
     * Aplica fixCodeBlockEscapes + stripChatCodeWrappers para garantir
     * que wrappers do chat nao vazem para o preview de documentacao.
     */
    protected updatePreview(content: string) {
        if (!this.contentPreview) return;
        this.contentPreview.innerHTML = stripChatCodeWrappers(fixCodeBlockEscapes(content));
    }

    /**
     * Copia o HTML gerado para a area de transferencia de forma rica.
     * Permite colar formatado colorido no Word/Outlook e colar puro em blocos de texto.
     */
    async copyContent() {
        if (!this.lastGeneratedContent) {
            window.$events.emit('error', 'Nenhum conteudo para copiar.');
            return;
        }

        try {
            const htmlContent = this.lastGeneratedContent;
            const plainContent = htmlToPlainText(htmlContent);

            const clipboardItem = new ClipboardItem({
                "text/html": new Blob([htmlContent], { type: "text/html" }),
                "text/plain": new Blob([plainContent], { type: "text/plain" })
            });

            await navigator.clipboard.write([clipboardItem]);

            if (this.copyButton) {
                const originalHTML = this.copyButton.innerHTML;
                this.copyButton.innerHTML = 'Copiado!';
                setTimeout(() => {
                    if (this.copyButton) {
                        this.copyButton.innerHTML = originalHTML;
                    }
                }, 2000);
            }
            window.$events.emit('success', 'Documento copiado! Você pode colar no Word ou Outlook.');
        } catch (err: any) {
            aiLogger.error('Erro ao copiar para clipboard:', err);
            // Fallback para navagadores que não suportam Clipboard API avançada (ou restritiva)
            try {
                await navigator.clipboard.writeText(this.lastGeneratedContent);
                window.$events.emit('success', 'Texto fonte copiado como fallback.');
            } catch (fallbackErr) {
                window.$events.emit('error', 'Erro completo ao copiar: ' + (err.message || 'Desconhecido'));
            }
        }
    }

    /**
     * Aplica o conteudo HTML gerado no editor usando o evento padrao do BookStack.
     * O HTML será denormalizado pela função htmlToDom() no pipeline do editor.
     */
    useContent() {
        if (!this.lastGeneratedContent) {
            window.$events.emit('error', 'Nenhum conteudo para aplicar.');
            return;
        }

        // Enviar o HTML completo para o editor
        window.$events.emit('editor::replace', {
            html: this.lastGeneratedContent,
            markdown: ''
        });

        if (this.modal) {
            this.modal.hide();
        }
        window.$events.emit('success', 'Conteudo aplicado com sucesso no editor!');
    }
}
